// screens/Delivery.tsx — Chunk PRG-W, plate "Delivery": one card per
// programme Atlas is running, hand-fed from a plan. Title, state word, day
// N of M, waiting-for-yes count, missed count — never a colour alone. The
// empty state teaches by shape: a ghost card drawn in the same grid a real
// programme would occupy, with no instructional caption.
import { useEffect, useState } from 'preact/hooks';
import { ApiError, listProgrammes, type ProgrammeCardApi, type ProgrammeState } from '../lib/api';
import './Delivery.css';

export interface DeliveryProps {
  tenantId: string;
  onOpenProgramme: (id: string) => void;
}

type LoadState =
  | { phase: 'loading' }
  | { phase: 'error'; message: string }
  | { phase: 'ready'; programmes: ProgrammeCardApi[] };

/** The state, in a word — never implied by colour alone. */
function stateWord(state: ProgrammeState): string {
  switch (state) {
    case 'proposed':
      return 'waiting for your yes';
    case 'running':
      return 'running';
    case 'paused':
      return 'paused';
    case 'completed':
      return 'completed';
    case 'withdrawn':
      return 'withdrawn';
    case 'declined':
      return 'declined';
    default:
      return state;
  }
}

export function Delivery({ tenantId, onOpenProgramme }: DeliveryProps) {
  const [load, setLoad] = useState<LoadState>({ phase: 'loading' });

  useEffect(() => {
    let cancelled = false;
    setLoad({ phase: 'loading' });
    listProgrammes(tenantId)
      .then((programmes) => {
        if (!cancelled) setLoad({ phase: 'ready', programmes });
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        setLoad({
          phase: 'error',
          message: err instanceof ApiError ? (err.serverMessage ?? err.message) : 'the programmes could not be read',
        });
      });
    return () => {
      cancelled = true;
    };
  }, [tenantId]);

  if (load.phase === 'loading') {
    return <div class="delivery" aria-hidden="true" />;
  }

  if (load.phase === 'error') {
    return (
      <div class="delivery">
        <div class="delivery__say">
          The programmes could not be read.
          <br />
          <span class="delivery__say-soft">{load.message}</span>
        </div>
      </div>
    );
  }

  const { programmes } = load;

  if (programmes.length === 0) {
    return (
      <div class="delivery">
        <div class="delivery__card delivery__card--ghost" aria-hidden="true">
          <div class="delivery__ghost-line delivery__ghost-line--title" />
          <div class="delivery__ghost-line delivery__ghost-line--sub" />
        </div>
        <div class="delivery__say">
          Nothing running yet.
          <br />
          <span class="delivery__say-soft">A plan handed to the Knot comes back here as one programme.</span>
        </div>
      </div>
    );
  }

  return (
    <div class="delivery">
      <div class="delivery__list">
        {programmes.map((p) => (
          <button type="button" class="delivery__card" key={p.id} onClick={() => onOpenProgramme(p.id)}>
            <div class="delivery__card-title">{p.title}</div>
            <div class="delivery__card-row">
              <span class="delivery__card-state">{stateWord(p.state)}</span>
              {p.dayN !== null ? (
                <span class="delivery__card-day">
                  day {p.dayN}
                  {p.dayTotal !== null ? ` of ${p.dayTotal}` : ''}
                </span>
              ) : null}
            </div>
            <div class="delivery__card-row delivery__card-row--counts">
              <span class={p.waitingForYes > 0 ? 'delivery__count delivery__count--yes' : 'delivery__count'}>
                {p.waitingForYes} waiting for your yes
              </span>
              <span class={p.missed > 0 ? 'delivery__count delivery__count--missed' : 'delivery__count'}>
                {p.missed} missed
              </span>
            </div>
          </button>
        ))}
      </div>
    </div>
  );
}
